import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchQuestions,
  fetchQuestion,
  fetchStats,
  fetchFilterOptions,
} from '@/lib/api';
import type { QuestionFilters } from '@/types';

const STALE_TIME = 30 * 1000; // 30 seconds

/**
 * Hook for fetching paginated questions
 */
export function useQuestions(
  filters: QuestionFilters = {},
  page = 1,
  pageSize = 20,
  sortBy = 'timestamp',
  sortOrder: 'asc' | 'desc' = 'desc'
) {
  return useQuery({
    queryKey: ['questions', filters, page, pageSize, sortBy, sortOrder],
    queryFn: () => fetchQuestions(filters, page, pageSize, sortBy, sortOrder),
    staleTime: STALE_TIME,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook for fetching single question
 */
export function useQuestion(id: string | undefined) {
  return useQuery({
    queryKey: ['question', id],
    queryFn: () => fetchQuestion(id!),
    enabled: !!id,
    staleTime: STALE_TIME,
  });
}

/**
 * Hook for fetching question stats
 */
export function useQuestionStats(filters: QuestionFilters = {}) {
  return useQuery({
    queryKey: ['questionStats', filters],
    queryFn: () => fetchStats(filters),
    staleTime: STALE_TIME,
  });
}

/**
 * Hook for fetching filter options
 */
export function useFilterOptions() {
  return useQuery({
    queryKey: ['filterOptions'],
    queryFn: fetchFilterOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for invalidating questions cache
 */
export function useInvalidateQuestions() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['questions'] });
    queryClient.invalidateQueries({ queryKey: ['questionStats'] });
  };
}
